// noinspection CssInvalidPropertyValue

import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
  MdHistory,
  MdOutlineCode,
  MdOutlineRemoveRedEye,
} from 'react-icons/md';
import { TbPencil } from 'react-icons/tb';
import { RiGuideLine } from 'react-icons/ri';
import { IoMdCheckmark, IoMdClose } from 'react-icons/io';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { AiComponentPrompt } from '@/features/editor/widgets/shared/ai-editing-layout/tab-prompt';
import { AiComponentCode } from '@/features/editor/widgets/shared/ai-editing-layout/tab-code';
import { AiComponentVisualEdit } from '@/features/editor/widgets/shared/ai-editing-layout/tab-visual-edit';
import { PopConfirm } from '@/components/pop-confirm';
import { openLinkInNewTab } from '@/utils/browser';
import { DeviceType } from '@/utils/definitions';
import { TabSelect } from '@/components/tab-select';
import { Flexbox } from '@/components/flex';
import { CheckpointPopover } from '@/features/ai-agents/components/checkpoint-popover';
import { HistoryCheckpoint } from '@/features/ai-agents/shared/use-ai-metadata-api';
import { AiComponentTab } from '@/features/editor/widgets/shared/ai-editing-layout/types';
import { INSPECTOR_WRAPPER_ID } from '@/features/editor/inspector';
import { ElementConfig } from '@/features/editor/widgets/shared/ai-editing-layout/element-config';
import { NormalTooltip } from '@/components/tooltip';
import { breakpoints } from '@/components/responsive';
import { SaveWidgetPopover } from '@/features/editor/widgets/shared/ai-editing-layout/save-widget-popover';
import { useTabContainerState } from '@/features/editor/widgets/shared/ai-editing-layout/hooks/use-tab-container-state';

export function AiEditorTabContainer() {
  const {
    device,
    portalElement,
    isHistoryEnabled,
    tabs,
    wrapperClasses,
    outOfChat,
    visualEditBtnState,
    logicBtnState,
    isCheckpointOpen,
    setIsCheckpointOpen,
    wrapperRef,
    newChange,
    hasChanges,
    currentSessionId,
    firstSessionId,
    lastRunId,
    hasVisualEditManifest,
    hasVisualElements,
    hasLogicElements,
    isBuilder,
    selectedTab,
    selectTab,
    selectedCodeTab,
    onSelectedCodeTab,
    isFullscreen,
    isComponentReady,
    discardChanges,
    applyChanges,
    discardLocalChanges,
    restoreCheckpoint,
    saveLocalChanges,
    editorConfig,
    previewTemp,
    getCurrentVariant,
  } = useTabContainerState();

  const onBackToChat = () => {
    selectTab(AiComponentTab.PROMPT);
  };

  const discardChangesAsync = async () => {
    discardChanges();
    return Promise.resolve();
  };

  const discardLocalChangesAsync = async () => {
    discardLocalChanges();
    return Promise.resolve();
  };

  const onRestoreCheckpoint = async (checkpoint: HistoryCheckpoint) => {
    if (checkpoint) {
      await restoreCheckpoint(checkpoint);
    }
    return Promise.resolve();
  };

  const onPreview = async () => {
    const link = await previewTemp(newChange.value);
    const variant = getCurrentVariant();
    openLinkInNewTab(link, variant.name);
  };

  return (
    <Wrapper ref={wrapperRef} className={wrapperClasses.value}>
      {portalElement}
      <GlobalStyles />
      <HeaderWrapper>
        <style>
          {`
          .header-row {
            container-type: inline-size;
            container-name: header-row;
          }
          @container header-row (max-width: 539px) {
              #ai-apply-changes-btn {
                padding: 0 0.5rem;
                width: 1.8rem;
              }
              #ai-apply-changes-btn .text {
                display: none;
              }
          }
            `}
        </style>
        <HeaderRow
          className={
            device === DeviceType.Mobile
              ? `mobile header-row`
              : `desktop header-row`
          }
        >
          <HeaderLeftSection
            className={device === DeviceType.Mobile ? `mobile` : `desktop`}
          >
            <AiChatTabButton
              id="ai-edit-visual-btn"
              disabled={
                !isComponentReady.value ||
                !hasVisualEditManifest.value ||
                !hasVisualElements.value
              }
              onClick={() => selectTab(AiComponentTab.EDIT)}
              className={visualEditBtnState.value}
            >
              <TbPencil size={13} />
              Design
            </AiChatTabButton>
            <AiChatTabButton
              id="ai-edit-logic-btn"
              disabled={
                !isComponentReady.value ||
                !hasVisualEditManifest.value ||
                !hasLogicElements.value
              }
              onClick={() => selectTab(AiComponentTab.LOGIC)}
              className={logicBtnState.value}
            >
              <RiGuideLine size={16} />
              Logic
            </AiChatTabButton>
            <AiChatTabButton
              id="ai-edit-code-btn"
              disabled={!isComponentReady.value}
              onClick={() => selectTab(AiComponentTab.CODE)}
              className={
                selectedTab.value === AiComponentTab.CODE ? `selected` : ``
              }
            >
              <MdOutlineCode size={16} />
              Code
            </AiChatTabButton>

            {isComponentReady.value && isBuilder && <SaveWidgetPopover />}

            <NormalTooltip id="tt-history" description="Show Chat History" />
            <Popover
              onClickOutside={() => setIsCheckpointOpen((prev) => !prev)}
              positions={[`bottom`, `left`]}
              isOpen={isCheckpointOpen}
              containerStyle={{ zIndex: `10000` }}
              boundaryElement={wrapperRef.current}
              content={({ position, childRect, popoverRect }) => (
                <ArrowContainer
                  position={position}
                  childRect={childRect}
                  popoverRect={popoverRect}
                  arrowColor="white"
                  arrowStyle={{
                    top: `0.1rem`,
                    zIndex: `9999`,
                    borderBottomColor: `rgba(222, 222, 222, 0.8)`,
                    filter: `drop-shadow(0px 1px 6px rgba(0, 0, 0, 0.1))`,
                  }}
                  arrowClassName="change-target-arrow"
                  arrowSize={10}
                >
                  <CheckpointPopover
                    currentSessionId={currentSessionId}
                    firstSessionId={firstSessionId}
                    lastRunId={lastRunId}
                    onRestoreSession={onRestoreCheckpoint}
                  />
                </ArrowContainer>
              )}
            >
              <IconButton
                data-tip
                data-for="tt-history"
                style={{
                  opacity: isHistoryEnabled ? `1` : `0`,
                  pointerEvents: isHistoryEnabled ? `auto` : `none`,
                }}
                className={isCheckpointOpen ? `open` : ``}
                onClick={() => setIsCheckpointOpen((prev) => !prev)}
              >
                <MdHistory
                  size={24}
                  color={isCheckpointOpen ? `#fff` : `#535a61`}
                  className={`history clickable ${
                    isComponentReady.value ? `` : `disabled`
                  }`}
                />
              </IconButton>
            </Popover>
            <NormalTooltip id="tt-preview" description="Open Live Preview" />
            <IconButton data-tip data-for="tt-preview" onClick={onPreview}>
              <MdOutlineRemoveRedEye
                size={24}
                color="#535a61"
                className={`preview clickable ${
                  isComponentReady.value ? `` : `disabled`
                }`}
              />
            </IconButton>
          </HeaderLeftSection>
          <div />
          <HeaderRightSection>
            {selectedTab.value === AiComponentTab.CODE &&
              (isFullscreen.value || device === DeviceType.Mobile) && (
                <TabSelect
                  tabs={tabs}
                  defaultValue={selectedCodeTab.value}
                  onTabSelected={(_, val) =>
                    onSelectedCodeTab(val as `HTML` | `CSS` | `JS`)
                  }
                  selectedColor="#000000"
                  unselectedColor="#444444"
                  tabFont="Inter"
                  tabWidth={10}
                  tabHeight={3.5}
                  tabPadding="0"
                  containerPadding={0}
                  tabRadius={1}
                  tabFontSize="1rem"
                />
              )}
            {outOfChat.value && (
              <BackToChat onClick={onBackToChat}>Back to chat</BackToChat>
            )}
            {!outOfChat.value && (
              <>
                {hasChanges.value ? (
                  <PopConfirm
                    text="Are you sure you want to close? changes made to this component will be discarded"
                    onConfirm={discardChangesAsync}
                  >
                    <HeaderButton className="invert">
                      <IoMdClose size={20} className="close-btn" />
                    </HeaderButton>
                  </PopConfirm>
                ) : (
                  <HeaderButton className="invert" onClick={discardChanges}>
                    <IoMdClose size={20} className="close-btn" />
                  </HeaderButton>
                )}
                {isComponentReady.value && hasChanges.value && (
                  <HeaderButton
                    className="long"
                    onClick={applyChanges}
                    id="ai-apply-changes-btn"
                  >
                    <IoMdCheckmark size={20} className="save-btn" />
                    <span className="text">Update</span>
                  </HeaderButton>
                )}
              </>
            )}
          </HeaderRightSection>
        </HeaderRow>
        {!isFullscreen.value &&
          selectedTab.value === AiComponentTab.CODE &&
          device === DeviceType.Desktop && (
            <Flexbox align="center" justify="center">
              <TabSelect
                tabs={tabs}
                defaultValue={selectedCodeTab.value}
                onTabSelected={(_, val) =>
                  onSelectedCodeTab(val as `HTML` | `CSS` | `JS`)
                }
                selectedColor="#000000"
                unselectedColor="#444444"
                tabFont="Inter"
                tabWidth={15.8}
                tabHeight={3.5}
                tabPadding="0"
                containerPadding={0}
                tabRadius={1}
                tabFontSize="1rem"
              />
            </Flexbox>
          )}
        <Seperator />
      </HeaderWrapper>
      <PromptWrapper
        className={`${
          selectedTab.value === AiComponentTab.PROMPT ? `` : `hide`
        } ${device === DeviceType.Mobile ? `mobile` : `desktop`}`}
      >
        <AiComponentPrompt />
      </PromptWrapper>
      {selectedTab?.value === AiComponentTab.CODE && <AiComponentCode />}
      {selectedTab?.value === AiComponentTab.EDIT && <AiComponentVisualEdit />}
      {selectedTab?.value === AiComponentTab.LOGIC && (
        <AiComponentVisualEdit logicOnly />
      )}
      {selectedTab?.value !== AiComponentTab.PROMPT && hasChanges?.value && (
        <FooterWrapper
          className={device === DeviceType.Desktop ? `desktop` : ``}
        >
          <FooterButton onClick={discardLocalChangesAsync} className="outline">
            Discard
          </FooterButton>
          <FooterButton onClick={saveLocalChanges}>Save</FooterButton>
        </FooterWrapper>
      )}
      <ElementConfig
        isVisible={isComponentReady.value && hasVisualEditManifest.value}
        editorConfig={editorConfig}
      />
    </Wrapper>
  );
}

const GlobalStyles = createGlobalStyle`
  #${INSPECTOR_WRAPPER_ID} {
    padding: 0 1.5rem !important;
  }
`;

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  border-radius: 1.5rem;
  background-color: white;

  display: flex;
  flex-direction: column;

  overflow: hidden;
  max-height: 100%;

  &.fullscreen {
    width: 100% !important;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background-color: white;
  }

  .visual-edit-tab {
    width: 100%;
    padding: 0.5rem;
    margin: 0 auto;
  }

  &&.mobile-visual-edit {
    .visual-edit-tab {
      width: 78%;
      margin: 0 auto;
      padding: 1rem !important;
    }
  }

  transition: width 1s ease-in-out;
`;

const Seperator = styled.div`
  display: block;
  width: 100%;
  height: 1px;
  background-color: rgb(222, 222, 222, 0.4);
  position: relative;
`;

export const IconButton = styled.button`
  background: transparent;
  border: none;
  outline: none;
  appearance: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  cursor: pointer;
  border-radius: 1rem;
  padding: 0 0.5rem;

  &&.open {
    background: rgb(39, 92, 243);
    box-shadow: 0 1px 6px 2px rgba(39, 92, 243, 0.2);
  }
`;

const PromptWrapper = styled.div`
  display: flex;
  max-height: 100%;
  height: 100%;
  overflow: hidden;

  mask-image: linear-gradient(
    var(--mask-direction, to bottom),
    hsl(0 0% 0% / 0),
    hsl(0 0% 0% / 1) 8%,
    hsl(0 0% 0% / 1) 100%,
    hsl(0 0% 0% / 0)
  );

  &&.mobile {
    padding: 0 2rem;
  }

  &.hide {
    display: none;
  }
`;

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .tab-select-container {
    background-color: rgb(233, 233, 233);
  }

  .tab-select-selector {
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 8px 10px 0 rgba(105, 122, 231, 0.11);
  }

  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const HeaderRow = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 1.2rem;
  align-items: center;
`;

const HeaderLeftSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  &&.desktop {
    gap: 0.5rem;

    ${breakpoints.down(1600)} {
      [id*='ai-edit-'] {
        padding: 0.6rem 1rem;
      }
    }
  }
`;

const HeaderRightSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const BackToChat = styled.button`
  color: black;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  text-decoration: underline;
  letter-spacing: -0.55px;
  cursor: pointer;
`;

const AiChatTabButton = styled.button`
  appearance: none;
  outline: none;
  color: #535a61;
  background-color: transparent;
  position: relative;
  border-radius: 3rem;
  padding: 0.6rem 1.4rem;
  font-family: Inter, sans-serif;
  font-weight: bold;
  font-size: 1.1rem;

  box-shadow: 0 6px 7px 0 rgba(151, 210, 206, 0.1);
  border: solid 2px #f0f1f2;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;

  cursor: pointer;
  user-select: none;

  height: 3.6rem;

  &:hover {
    background-color: #eef0ff;
    border-color: #eef0ff;
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0),
      0 6px 7px 0 rgba(151, 153, 210, 0.22), 0 0.5px 0.5px 0 rgba(0, 0, 0, 0.15);
  }

  &:active {
    background-color: #1f4eff;
    color: #fff;
    border-color: #1f4eff;
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0),
      0 6px 7px 0 rgba(151, 153, 210, 0.22), 0 0.5px 0.5px 0 rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  &&.selected {
    font-weight: bold;
    background-color: #1f4eff;
    color: #fff;
    border-color: #1f4eff;
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0),
      0 6px 7px 0 rgba(151, 153, 210, 0.22), 0 0.5px 0.5px 0 rgba(0, 0, 0, 0.15);
  }

  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;
`;

const HeaderButton = styled.button`
  cursor: pointer;
  appearance: none;
  display: flex;
  height: 2.8rem;
  width: 2.8rem;
  flex-direction: row;
  gap: 0.4rem;
  padding: 0;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  border-radius: 50%;
  letter-spacing: -0.56px;
  font-family: Inter, sans-serif;
  font-size: 1.1rem;
  text-transform: uppercase;
  font-weight: 600;

  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0), 0 6px 7px 0 rgba(151, 210, 206, 0.1);
  background-color: #000;
  color: white;
  border: 2px solid #000;

  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;

  &&.long {
    width: auto;
    border-radius: 3rem;
    padding: 0rem 1.8rem 0rem 1.3rem;
    color: white;
    transition: color 0.2s ease-in-out;
  }

  &&:hover {
    color: black;
    background-color: white;
    border-color: black;
  }

  &&:active {
    transform: scale(0.95);
  }

  &&.invert {
    border-color: white;
    filter: invert(1);
  }
`;

const FooterWrapper = styled.div`
  position: absolute;
  width: 75%;
  bottom: 2rem;
  left: 50%;
  padding: 1.4rem 2rem;
  border-radius: 2rem;
  transform: translateX(-50%);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.1);

  &&.desktop {
    width: 90%;
  }

  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1.5rem;

  animation: reveal-bottom-up 0.6s ease-in-out;
  opacity: 0;
  animation-fill-mode: forwards;

  @keyframes reveal-bottom-up {
    from {
      opacity: 0;
      transform: translate(-50%, 4rem);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
`;

const FooterButton = styled.button`
  cursor: pointer;
  outline: none;
  appearance: none;
  background: black;
  border: 1px solid black;
  color: white;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 1.4rem;
  padding: 1rem 3rem;
  border-radius: 1rem;
  width: 100%;

  &&.outline {
    background: transparent;
    color: black;
  }

  transition: opacity 0.2s ease-in-out;

  &&:hover {
    opacity: 0.8;
  }

  &&:active {
    opacity: 1;
  }
`;
